import {useQuery} from '@apollo/client'
import {useEffect, useState} from 'react'
import {GET_BOTMAKER_SETTINGS} from '../../../gql/queries/botmaker'
import {Button, Form, Modal} from 'react-bootstrap'
import {CampaignChannel, IMessageTemplate} from '../../../types/campaigns'

type TemplateFormValues = {
  name: string
  description?: string
  channel: CampaignChannel
  subject?: string
  body: string
  variables?: string[]
  metadata?: Record<string, any>
  namespace?: string
}

type Props = {
  show: boolean
  onHide: () => void
  onSubmit: (values: TemplateFormValues) => Promise<void>
  template?: IMessageTemplate | null
  loading?: boolean
  defaultChannel?: CampaignChannel
}

const channelOptions: CampaignChannel[] = ['SMS', 'EMAIL', 'WHATSAPP', 'TELEGRAM']

export const TemplateFormModal = ({show, onHide, onSubmit, template, loading, defaultChannel}: Props) => {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [channel, setChannel] = useState<CampaignChannel>('EMAIL')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [variables, setVariables] = useState('')
  const [metadataJson, setMetadataJson] = useState('{}')
  const [namespace, setNamespace] = useState('')

  const {data: settingsData} = useQuery(GET_BOTMAKER_SETTINGS, {
    skip: channel !== 'WHATSAPP',
  })

  useEffect(() => {
    if (settingsData?.botmakerSettings?.namespace && !template) {
      setNamespace(settingsData.botmakerSettings.namespace)
    }
  }, [settingsData, template])

  useEffect(() => {
    if (!template) {
      setName('')
      setDescription('')
      setChannel(defaultChannel || 'EMAIL')
      setSubject('')
      setBody('')
      setVariables('')
      setMetadataJson('{}')
      setNamespace('')
      return
    }
    setName(template.name)
    setDescription(template.description ?? '')
    setChannel(template.channel)
    setSubject(template.subject ?? '')
    setBody(template.body)
    setVariables(
      Array.isArray(template.variables)
        ? template.variables.join(', ')
        : (template.variables || '')
    )
    setMetadataJson(JSON.stringify(template.metadata ?? {}, null, 2))
    setNamespace(template.metadata?.namespace || '')
  }, [template, defaultChannel])

  const handleSubmit = async () => {
    const payload: TemplateFormValues = {
      name,
      description,
      channel,
      subject: channel === 'EMAIL' ? subject : undefined,
      body,
      variables: variables
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean),
      metadata: (() => {
        try {
          const parsed = JSON.parse(metadataJson || '{}')
          return parsed
        } catch (error) {
          return {}
        }
      })(),
      namespace: channel === 'WHATSAPP' ? namespace : undefined,
    }
    await onSubmit(payload)
  }

  return (
    <Modal show={show} onHide={onHide} size='lg' centered>
      <Modal.Header closeButton>
        <Modal.Title>{template ? 'Edit template' : 'Create template'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='row g-4'>
          <div className='col-md-6'>
            <Form.Group className='mb-4'>
              <Form.Label>Name</Form.Label>
              <Form.Control value={name} onChange={(event) => setName(event.target.value)} placeholder='Renewal reminder' />
            </Form.Group>
            <Form.Group className='mb-4'>
              <Form.Label>Description</Form.Label>
              <Form.Control
                as='textarea'
                rows={3}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </Form.Group>
            <Form.Group className='mb-4'>
              <Form.Label>Channel</Form.Label>
              <Form.Select value={channel} onChange={(event) => setChannel(event.target.value as CampaignChannel)}>
                {channelOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            {channel === 'WHATSAPP' && (
              <Form.Group className='mb-4'>
                <Form.Label>Namespace (WhatsApp)</Form.Label>
                <Form.Control 
                  value={namespace} 
                  onChange={(event) => setNamespace(event.target.value)} 
                  placeholder='e.g. f1a2b3c4_d5e6_7f8g...' 
                  readOnly={!!settingsData?.botmakerSettings?.namespace}
                  disabled={!!settingsData?.botmakerSettings?.namespace}
                />
                <div className='form-text text-danger'>
                  Required for Meta WhatsApp templates. Look for it in Botmaker or Meta Business Suite.
                </div>
              </Form.Group>
            )}
            {channel === 'EMAIL' && (
              <Form.Group className='mb-4'>
                <Form.Label>Subject</Form.Label>
                <Form.Control value={subject} onChange={(event) => setSubject(event.target.value)} placeholder='Stay covered with us' />
              </Form.Group>
            )}
            <Form.Group className='mb-4'>
              <Form.Label>Variables (comma separated)</Form.Label>
              <Form.Control
                placeholder='e.g. firstName,lastName,policyNumber'
                value={variables}
                onChange={(event) => setVariables(event.target.value)}
              />
            </Form.Group>
          </div>
          <div className='col-md-6'>
            <Form.Group className='mb-4'>
              <Form.Label>Body</Form.Label>
              <Form.Control as='textarea' rows={10} value={body} onChange={(event) => setBody(event.target.value)} />
            </Form.Group>
            <Form.Group>
              <Form.Label>Metadata (JSON)</Form.Label>
              <Form.Control
                as='textarea'
                rows={5}
                value={metadataJson}
                onChange={(event) => setMetadataJson(event.target.value)}
              />
            </Form.Group>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='light' onClick={onHide} disabled={loading}>
          Cancel
        </Button>
        <Button variant='primary' onClick={handleSubmit} disabled={loading || !name || !body}>
          {template ? 'Save template' : 'Create template'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
